import { Line } from 'react-chartjs-2'

import { ChartOptions, Plugin } from 'chart.js'

const chartOpts: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },

  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: {
        color: '#34414F',
        font: { size: 12 },
        boxWidth: 24,
        boxHeight: 2,
        padding: 8,
        usePointStyle: false
      }
    },
    tooltip: {
      backgroundColor: 'rgba(2, 6, 23, 0.95)',
      titleFont: { family: "'JetBrains Mono', monospace", size: 11, weight: 'bold' },
      bodyFont: { family: "'JetBrains Mono', monospace", size: 11 },
      padding: 8,
      cornerRadius: 4,
      borderColor: 'rgba(6,182,212,0.2)',
      borderWidth: 1,
      displayColors: true
    }
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 10 }, color: '#34414F', maxRotation: 0 }
    },
    y: {
      beginAtZero: true,
      border: { display: false },
      grid: { color: '#E4E8EE' },
      ticks: { font: { size: 10 }, color: '#34414F', padding: 6, count: 6 }
    }
  },
  elements: {
    line: {
      borderWidth: 2.5,
      tension: 0
    },
    point: {
      radius: 0,
      hoverRadius: 4,
      hitRadius: 8
    }
  }
}

interface Props {
  labels: string[]
  values: (string | number)[]
  preValues: (string | number)[]
  currentValueLabel: string
  previousValueLabel: string
}

const LineChart = ({ labels, values, preValues, currentValueLabel, previousValueLabel }: Props) => {
  const brandGradientLegendPlugin: Plugin<'line'> = {
    id: 'brandGradientLegend',
    afterDraw(chart) {
      const legend = (chart as any).legend
      if (!legend?.legendHitBoxes) return
      legend.legendHitBoxes.forEach(
        (hitBox: { left: number; top: number; height: number }, i: number) => {
          const label = legend.legendItems?.[i]?.text
          const { ctx } = chart
          const x = hitBox.left
          const y = hitBox.top + hitBox.height / 2
          ctx.save()
          if (label === currentValueLabel) {
            const g = ctx.createLinearGradient(x, 0, x + 24, 0)
            g.addColorStop(0, '#262680')
            g.addColorStop(0.6, '#1592d1')
            g.addColorStop(1.0, '#43e0c3')
            ctx.strokeStyle = g
            ctx.lineWidth = 2
            ctx.setLineDash([])
          } else if (label === previousValueLabel) {
            ctx.strokeStyle = '#D3D9E1'
            ctx.lineWidth = 1.5
            ctx.setLineDash([4, 2])
          } else {
            ctx.restore()
            return
          }
          ctx.beginPath()
          ctx.moveTo(x, y)
          ctx.lineTo(x + 24, y)
          ctx.stroke()
          ctx.restore()
        }
      )
    }
  }
  return (
    <Line
      data={{
        labels: labels,
        datasets: [
          {
            label: currentValueLabel,
            data: values,
            fill: true,
            backgroundColor: (ctx) => {
              const g = ctx.chart.ctx.createLinearGradient(0, 0, 0, 160)
              g.addColorStop(0, 'rgba(16,185,129,0.2)')
              g.addColorStop(1, 'rgba(16,185,129,0)')
              return g
            },
            borderColor: (ctx) => {
              const { ctx: canvasCtx, chartArea } = ctx.chart
              if (!chartArea) return '#1592d1'
              const g = canvasCtx.createLinearGradient(chartArea.left, 0, chartArea.right, 0)
              g.addColorStop(0.12, '#262680')
              g.addColorStop(0.72, '#1592d1')
              g.addColorStop(1.0, '#43e0c3')
              return g
            },
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            pointHoverBackgroundColor: '#1592d1',
            pointHoverBorderColor: '#0f172a',
            pointHoverBorderWidth: 2,
            tension: 0.4
          },
          {
            label: previousValueLabel,
            data: preValues,
            fill: false,
            borderColor: '#D3D9E1',
            borderWidth: 1.5,
            borderDash: [4, 3],
            pointRadius: 0,
            pointHoverRadius: 3,
            pointHoverBackgroundColor: '#D3D9E1',
            pointHoverBorderColor: '#0f172a',
            pointHoverBorderWidth: 1,
            tension: 0.4
          }
        ]
      }}
      options={chartOpts}
      plugins={[brandGradientLegendPlugin]}
    />
  )
}

export default LineChart
